import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import type { Quiz } from '../services/api';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import { SITE_LOGO_URL, SITE_NAME, SITE_ORIGIN } from '../seo/site';
import i18n from '../utils/i18n';

interface QuizPlayerSeoProps {
  quiz: Quiz;
  quizId: string;
  questionCount: number;
  language: string;
}

/** Overrides DocumentMeta with quiz-specific title, canonical, OG, Article/WebPage JSON-LD. */
const QuizPlayerSeo: React.FC<QuizPlayerSeoProps> = ({
  quiz,
  quizId,
  questionCount,
  language,
}) => {
  const headline = getQuizFullDisplayCaption(quiz, language);
  const title = useMemo(
    () =>
      i18n.t('seo.quiz.documentTitle', {
        headline,
        siteName: SITE_NAME,
        defaultValue: `${headline} — ${SITE_NAME}`,
      }),
    [headline, language, i18n.language]
  );
  const description = useMemo(() => {
    const raw = i18n.t('seo.quiz.metaDescription', {
      title: headline,
      count: questionCount,
      difficulty: quiz.difficulty,
      siteName: SITE_NAME,
      defaultValue: `${headline}: ${questionCount}-question ${quiz.difficulty} quiz on ${SITE_NAME}. Timed play, XP rewards, achievements.`,
    });
    if (raw.length <= 200) return raw;
    return `${raw.slice(0, 196).trim()}…`;
  }, [headline, questionCount, quiz.difficulty, language, i18n.language]);
  const canonical = `${SITE_ORIGIN}/quiz/${quizId}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: headline,
        description,
        isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
        publisher: { '@id': `${SITE_ORIGIN}/#organization` },
        inLanguage: language.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en',
      },
      {
        '@type': 'Quiz',
        '@id': `${canonical}#quiz`,
        name: headline,
        educationalLevel: quiz.difficulty,
        numberOfQuestions: questionCount,
        isPartOf: { '@id': `${canonical}#webpage` },
      },
    ],
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="article" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={SITE_LOGO_URL} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={SITE_LOGO_URL} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default QuizPlayerSeo;
